import { Story, BlogPost, FAQCategory, LegalDoc } from '../types';
import { DocumentSnapshot } from 'firebase/firestore';
import { contentRepository } from '../../data/contentRepository';

export const contentService = {

  // --- STORIES ---
  async createStory(storyData: Partial<Story>, lifetimeHours: number, creator: { name: string; avatar: string }): Promise<{ success: boolean; message: string }> {
    try {
      if (!storyData.mediaUrl) return { success: false, message: 'Falta media' };
      const expiryDate = new Date(Date.now() + (lifetimeHours * 60 * 60 * 1000));
      const newStory: Story = {
        id: `s-${Date.now()}`,
        brandName: storyData.brandName || creator.name,
        brandAvatar: storyData.brandAvatar || creator.avatar,
        mediaUrl: storyData.mediaUrl,
        type: storyData.type || 'image',
        productId: storyData.productId || '',
        isViewed: false,
        duration: storyData.duration || 5000,
        createdAt: new Date().toISOString(),
        expiresAt: expiryDate.toISOString(),
        status: 'active',
        isAd: !!storyData.isAd
      };
      await contentRepository.addStory(newStory);
      return { success: true, message: 'Historia publicada' };
    } catch (e) {
      return { success: false, message: e instanceof Error ? e.message : 'Error al crear historia' };
    }
  },

  async getStories(): Promise<Story[]> {
    return contentRepository.getStories();
  },

  async deleteStory(id: string): Promise<void> {
    await contentRepository.deleteStory(id);
  },

  async deleteAllStories(): Promise<void> {
    await contentRepository.deleteAllStories();
  },

  async updateStoryStatus(id: string, status: 'active' | 'archived'): Promise<void> {
    await contentRepository.updateStoryStatus(id, status);
  },

  async markStoryViewed(id: string): Promise<void> {
    await contentRepository.markStoryViewed(id);
  },

  subscribeToStories(callback: (stories: Story[]) => void): () => void {
    return contentRepository.subscribeToStories(callback);
  },

  // --- BLOG POSTS ---
  async createOrUpdatePost(postData: Partial<BlogPost>, user: { name: string }): Promise<{ success: boolean; message: string }> {
    try {
      if (!postData.title || !postData.content) {
        return { success: false, message: 'Título y contenido requeridos' };
      }
      const post: BlogPost = {
        id: postData.id || `p-${Date.now()}`,
        title: postData.title,
        slug: postData.slug || postData.title.toLowerCase().replace(/\s+/g, '-') as string,
        excerpt: postData.excerpt || '',
        content: postData.content,
        imageUrl: postData.imageUrl || '',
        author: user.name,
        date: new Date().toLocaleDateString('es-AR'),
        likes: postData.likes || [],
        tags: postData.tags || [],
        linkedProductIds: postData.linkedProductIds || [],
        isPublished: postData.isPublished ?? true,
        status: postData.status || 'active',
        seoTitle: postData.seoTitle || postData.title || '',
        seoDescription: postData.seoDescription || postData.excerpt || ''
      };

      await contentRepository.savePost(post);
      return { success: true, message: 'Artículo guardado' };
    } catch (e) {
      return { success: false, message: e instanceof Error ? e.message : 'Error al procesar artículo' };
    }
  },

  async getAllPosts(cursor: DocumentSnapshot | null = null, pageSize: number = 10, status: 'active' | 'archived' = 'active'): Promise<{ posts: BlogPost[], lastDoc: DocumentSnapshot | null }> {
    return contentRepository.getAllPosts(cursor, pageSize, status);
  },

  async toggleArchivePost(postId: string, isArchived: boolean): Promise<void> {
    await contentRepository.toggleArchivePost(postId, isArchived);
  },

  async savePost(post: Partial<BlogPost>): Promise<string> {
    return contentRepository.savePost(post);
  },

  async deletePost(id: string): Promise<void> {
    await contentRepository.deletePost(id);
  },

  async addPosts(posts: BlogPost[]): Promise<void> {
    await contentRepository.addPosts(posts);
  },

  async deleteAllPosts(): Promise<void> {
    await contentRepository.deleteAllPosts();
  },

  async togglePostReaction(postId: string, userId: string): Promise<void> {
    await contentRepository.togglePostReaction(postId, userId);
  },

  subscribeToPosts(callback: (posts: BlogPost[]) => void): () => void {
    return contentRepository.subscribeToPosts(callback);
  },

  // --- STATIC CONTENT (Firebase Integrated) ---
  async getFAQs(): Promise<FAQCategory[]> {
    try {
      const faqs = await contentRepository.getFaqs();
      return faqs;
    } catch {
      return [];
    }
  },

  async saveFaq(faq: FAQCategory): Promise<void> {
    await contentRepository.saveFaq(faq);
  },

  async deleteAllFaqs(): Promise<void> {
    await contentRepository.deleteAllFaqs();
  },

  async getLegalDoc(type: string): Promise<LegalDoc | null> {
    try {
      const docs = await contentRepository.getLegalDocs();
      const doc = docs.find(d => d.id === type);
      return doc || null;
    } catch {
      return null;
    }
  },

  async saveLegalDoc(docData: LegalDoc): Promise<void> {
    await contentRepository.saveLegalDoc(docData);
  },

  async deleteAllLegalDocs(): Promise<void> {
    await contentRepository.deleteAllLegalDocs();
  }
};
